import { useCallback, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";

import { EVOKE_ROLES, USER_ROLES } from "static/constants";
import {
  setLoading,
  setSelectedRowIds,
  setTableFilters,
} from "app/store/tasks/slice/taskSlice";

const TAB_URL_MAP = {
  active: "/tasks",
  automated: "/tasks/automated",
  audited: "/tasks/audited",
  archived: "/tasks/archived",
  "audited-archived": "/tasks/audited-archived",
  "pending-approvals": "/tasks/pending-approvals",
};

const useTaskTabs = (selectedTab) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedRowIds } = useSelector((state) => state.task);
  const { currentUser } = useSelector((state) => state.auth);

  const role = localStorage.getItem("userRole");

  const tabStyles = useCallback(
    (currentPage) => ({
      border: selectedTab === currentPage ? "1px solid #E0E0E0" : "none",
      borderBottom:
        selectedTab === currentPage ? theme.palette.background.paper : "none",
      display: "block",
      backgroundColor:
        selectedTab === currentPage
          ? theme.palette.background.paper
          : "transparent",
    }),
    [selectedTab, theme]
  );

  const onTabChange = useCallback(
    (newTab) => {
      const url = TAB_URL_MAP[newTab] || "/tasks";

      dispatch(setLoading(true));
      dispatch(setSelectedRowIds([]));
      dispatch(setTableFilters([]));

      navigate(url);
    },
    [dispatch, navigate]
  );

  const archiveTab = useMemo(
    () => ({
      label: "Archived",
      scope: [...EVOKE_ROLES, USER_ROLES.CLINIC_ADMIN],
      value: "archived",
      style: tabStyles("archived"),
    }),
    [tabStyles]
  );

  const initialTabs = useMemo(
    () => [
      {
        label: "Active",
        scope: [...EVOKE_ROLES, USER_ROLES.CLINIC_ADMIN],
        value: "active",
        style: tabStyles("active"),
      },
      {
        label: "Automated Scheduling Tasks",
        scope: [...EVOKE_ROLES, USER_ROLES.CLINIC_ADMIN],
        value: "automated",
        style: tabStyles("automated"),
      },
    ],
    [tabStyles]
  );

  const tabs = useMemo(() => {
    const {
      audited_reports: showAuditedReportsTab,
      show_pending_approvals: showPendingApprovalsTab,
    } = currentUser?.user_prefs ?? {};

    const conditionalTabs = [];

    if (showAuditedReportsTab) {
      conditionalTabs.push(
        {
          label: "Audited Reports",
          scope: [USER_ROLES.ADMIN],
          value: "audited",
          style: tabStyles("audited"),
        },
        {
          label: "Audited Archived",
          scope: [USER_ROLES.ADMIN],
          value: "audited-archived",
          style: tabStyles("audited-archived"),
        }
      );
    }

    if (showPendingApprovalsTab) {
      conditionalTabs.push({
        label: "Pending Approvals",
        scope: [USER_ROLES.ADMIN],
        value: "pending-approvals",
        style: tabStyles("pending-approvals"),
      });
    }

    return [...initialTabs, archiveTab, ...conditionalTabs];
  }, [currentUser, initialTabs, archiveTab, tabStyles]);

  return {
    tabs,
    selectedRowIds,
    role,
    onTabChange,
  };
};

export default useTaskTabs;
